import * as React from 'react';
import Modal from 'react-modal';
import { MutationFn } from 'react-apollo';
import { InjectedRouter, PlainRoute } from 'react-router';

import { AdminDefaultClassSettings_organizations_classesDefaultSettings } from '../queries/__generated__/AdminDefaultClassSettings';
import { Location } from 'lib/ReactRouterTypes';
import { OrganizationType, ClassSearchability, ParticipantMessagingState } from 'globalTypes';
import {
  setOrgClassDefaults as setOrgClassDefaultsResultType,
  setOrgClassDefaultsVariables,
} from '../queries/__generated__/setOrgClassDefaults';
import Button from 'components/Button';
import Checkbox from 'components/Checkbox';
import ConfirmDialog from 'components/ConfirmDialog';
import Container from 'components/Container';
import DefaultClassSettingsConstants from '../constants/DefaultClassSettingsConstants';
import Divider from 'components/Divider';
import Field from 'components/Field';
import Label from 'components/Label';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Radio from 'components/Radio';
import Row from 'components/Row';
import Text from 'components/Text';
import TitleHeader from 'components/TitleHeader';
import Notice from 'components/Notice';

export type Props = {
  activeGroupCount: number;
  classesDefaultSettings: AdminDefaultClassSettings_organizations_classesDefaultSettings;
  orgIsDemoOnly: boolean;
  orgType: OrganizationType;
  orgUuid: string;
  route: PlainRoute;
  router: InjectedRouter;
  setOrgClassDefaults: MutationFn<setOrgClassDefaultsResultType, setOrgClassDefaultsVariables>;
};

type State = {
  backfillDialogIsOpen: boolean;
  backfillExistingClasses: boolean;
  classSearchabilityDefault: ClassSearchability;
  nextLocation?: Location;
  participantMessagingStateDefault: ParticipantMessagingState;
  requestToJoinEnabledDefault: boolean;
  unsavedChangesDialogIsOpen: boolean;
};

enum RequestToJoinEnabledEnum {
  true = 'true',
  false = 'false',
}

export const renderRadioSelectLabel = (primaryText: string, secondaryText: string, isRecommended?: boolean) => (
  <Text lineHeight="large" size="small">
    {primaryText}
    {isRecommended && (
      <Text color="thunder" italic weight="normal">
        {' '}
        - District default
      </Text>
    )}
    <Text element="div" color="thunder" weight="normal">
      {secondaryText}
    </Text>
  </Text>
);

export const renderRadioSelectField = (labelText: string, formElements: React.ReactElement[]) => (
  <Row>
    <Field>
      <Label>
        <Text size="small" weight="semibold">
          {labelText}
        </Text>
      </Label>
      {formElements}
    </Field>
  </Row>
);

export default class AdminDefaultClassSettingsView extends React.Component<Props, State> {
  state: State = {
    classSearchabilityDefault: this.props.classesDefaultSettings.classSearchabilityDefault,
    participantMessagingStateDefault: this.props.classesDefaultSettings
      .participantMessagingStateDefault,
    requestToJoinEnabledDefault: this.props.classesDefaultSettings.requestToJoinEnabledDefault,
    backfillExistingClasses: false,
    unsavedChangesDialogIsOpen: false,
    backfillDialogIsOpen: false,
  };

  componentDidMount() {
    // https://github.com/ReactTraining/react-router/blob/v3/docs/API.md#setrouteleavehookroute-hook
    // this registers `handleAttemptedRouteChange` to fire anytime the user tries to leave the current route
    this.props.router.setRouteLeaveHook(this.props.route, this.handleAttemptedRouteChange);
  }

  showUnsavedChangesDialog = () => {
    this.setState({ unsavedChangesDialogIsOpen: true });
  };

  hideUnsavedChangesDialog = () => {
    this.setState({ unsavedChangesDialogIsOpen: false });
  };

  showBackfillDialog = () => {
    this.setState({ backfillDialogIsOpen: true });
  };

  hideBackfillDialog = () => {
    this.setState({ backfillDialogIsOpen: false });
  };

  handleAttemptedRouteChange = (nextLocation?: Location) => {
    // if the user has changed their settings and isn't already viewing the unsaved changes dialog,
    // we should mount the dialog and store the nextLocation that the user attempted to visit
    if (this.userHasChangedSettings() && nextLocation && !this.state.unsavedChangesDialogIsOpen) {
      this.setState({ nextLocation }, this.showUnsavedChangesDialog);
      return false;
    }
  };

  forwardUserToNextLocation = () => {
    // we should only be calling this method from the confirmation dialog, so we *should* always
    // have a nextLocation, but we don't initialize this component with a nextLocation so we
    // need to perform this check
    const { nextLocation } = this.state;
    return nextLocation
      ? this.props.router.push(nextLocation)
      : console.error(
        'AdminDefaultClassSettingsView - tried to route user to nonexistent nextLocation'
      );
  };

  userHasChangedSettings = () =>
    this.state.classSearchabilityDefault !==
    this.props.classesDefaultSettings.classSearchabilityDefault ||
    this.state.participantMessagingStateDefault !==
    this.props.classesDefaultSettings.participantMessagingStateDefault ||
    this.state.requestToJoinEnabledDefault !==
    this.props.classesDefaultSettings.requestToJoinEnabledDefault ||
    this.state.backfillExistingClasses;

  handleSubmit = async () => {
    await this.props.setOrgClassDefaults({
      variables: {
        input: {
          orgUuid: this.props.orgUuid,
          orgRequestToJoinEnabledDefault: this.state.requestToJoinEnabledDefault,
          orgSearchabilityDefault: this.state.classSearchabilityDefault,
          orgParticipantMessagingDefault: this.state.participantMessagingStateDefault,
          backfillExistingClasses: this.state.backfillExistingClasses,
        },
      },
    });
  };

  handleClassSearchabilityChange = (newClassSearchability: ClassSearchability) => {
    this.setState({ classSearchabilityDefault: newClassSearchability });
  };

  handleParticipantMessagingStateChange = (
    newParticipantMessagingState: ParticipantMessagingState
  ) => {
    this.setState({ participantMessagingStateDefault: newParticipantMessagingState });
  };

  handleRequestToJoinEnabledChange = (newRequestToJoinEnabled: boolean) => {
    this.setState({ requestToJoinEnabledDefault: newRequestToJoinEnabled });
  };

  handleBackfillChange = (newBackfillExistingClasses: boolean) => {
    this.setState({ backfillExistingClasses: newBackfillExistingClasses });
  };

  resetBackfillCheckbox = () => {
    this.handleBackfillChange(false);
  };

  renderClassSearchabilityField = () => {
    const { orgIsDemoOnly, classesDefaultSettings } = this.props;
    const { classSearchabilityDefaultDistrictRecommendation } = classesDefaultSettings;
    const { classSearchabilityDefault } = this.state;
    const radioButtons = (Object.keys(
      ClassSearchability
    ) as ClassSearchability[]).map(searchability => (
      <Radio
        key={searchability}
        label={renderRadioSelectLabel(
          DefaultClassSettingsConstants.CLASS_SEARCHABILITY_TO_LABEL[searchability],
          DefaultClassSettingsConstants.CLASS_SEARCHABILITY_TO_HELPTEXT[searchability],
          searchability === classSearchabilityDefaultDistrictRecommendation
        )}
        checked={searchability === classSearchabilityDefault}
        onChange={() => this.handleClassSearchabilityChange(searchability)}
        data-test={`searchability-radio-${searchability}`}
        disabled={orgIsDemoOnly}
      />
    ));
    return renderRadioSelectField(
      DefaultClassSettingsConstants.HEADING_FOR_SETTING_TYPE.classSearchabilityDefault,
      radioButtons
    );
  };

  renderRequestToJoinEnabledField = () => {
    const { orgIsDemoOnly, classesDefaultSettings } = this.props;
    const { requestToJoinEnabledDefaultDistrictRecommendation } = classesDefaultSettings;
    const { requestToJoinEnabledDefault } = this.state;
    const radioButtons = (Object.keys(RequestToJoinEnabledEnum) as RequestToJoinEnabledEnum[]).map(
      requestToJoinOption => {
        const requestToJoinEnabled = requestToJoinOption === 'true';
        return (
          <Radio
            key={requestToJoinOption}
            label={renderRadioSelectLabel(
              DefaultClassSettingsConstants.REQUIRE_APPROVAL_TO_LABEL[requestToJoinOption],
              DefaultClassSettingsConstants.REQUIRE_APPROVAL_TO_HELPTEXT[requestToJoinOption],
              requestToJoinEnabledDefaultDistrictRecommendation === requestToJoinEnabled
            )}
            checked={requestToJoinEnabledDefault === requestToJoinEnabled}
            onChange={() => this.handleRequestToJoinEnabledChange(requestToJoinEnabled)}
            data-test={`requestToJoin-radio-${requestToJoinOption}`}
            disabled={orgIsDemoOnly}
          />
        );
      }
    );
    return renderRadioSelectField(
      DefaultClassSettingsConstants.HEADING_FOR_SETTING_TYPE.requestToJoinEnabledDefault,
      radioButtons
    );
  };

  renderParticipantMessagingStateField = () => {
    const { orgIsDemoOnly, classesDefaultSettings } = this.props;
    const { participantMessagingStateDefaultDistrictRecommendation } = classesDefaultSettings;
    const { participantMessagingStateDefault } = this.state;
    const radioButtons = (Object.keys(
      ParticipantMessagingState
    ) as ParticipantMessagingState[]).map(participantMessagingOption => (
      <Radio
        key={participantMessagingOption}
        label={renderRadioSelectLabel(
          DefaultClassSettingsConstants.PARTICIPANT_MESSAGING_TO_LABEL[participantMessagingOption],
          DefaultClassSettingsConstants.PARTICIPANT_MESSAGING_TO_HELPTEXT[
          participantMessagingOption
          ],
          participantMessagingOption === participantMessagingStateDefaultDistrictRecommendation
        )}
        checked={participantMessagingOption === participantMessagingStateDefault}
        onChange={() => this.handleParticipantMessagingStateChange(participantMessagingOption)}
        data-test={`participantMessaging-radio-${participantMessagingOption}`}
        disabled={orgIsDemoOnly}
      />
    ));
    return renderRadioSelectField(
      DefaultClassSettingsConstants.HEADING_FOR_SETTING_TYPE.participantMessagingStateDefault,
      radioButtons
    );
  };

  renderBackfillCheckbox = () => {
    const { backfillExistingClasses } = this.state;
    const { orgIsDemoOnly } = this.props;
    return (
      <Row size="xxxlarge">
        <Divider size="medium" />
        <Field>
          <Checkbox
            label={renderRadioSelectLabel(
              DefaultClassSettingsConstants.BACKFILL_LABEL,
              DefaultClassSettingsConstants.BACKFILL_TEXT_FOR_ORG_TYPE[this.props.orgType]
            )}
            checked={backfillExistingClasses}
            onChange={() => this.handleBackfillChange(!backfillExistingClasses)}
            data-test="backfill-checkbox"
            disabled={orgIsDemoOnly}
          />
        </Field>
        <Divider size="medium" />
      </Row>
    );
  };

  render() {
    const { orgIsDemoOnly } = this.props;
    const {
      unsavedChangesDialogIsOpen,
      backfillDialogIsOpen,
      backfillExistingClasses,
    } = this.state;
    return (
      <React.Fragment>
        <Modal isOpen={unsavedChangesDialogIsOpen}>
          <ConfirmDialog
            glyph="caution"
            glyphColor="lava"
            title="You haven’t saved your changes"
            subtext="Click save at the bottom of the screen and decide if you want to apply the changes to existing classes at your school."
            onConfirm={this.hideUnsavedChangesDialog}
            onCancel={this.forwardUserToNextLocation}
            actionButtonText="Go back"
            cancelButtonText="Discard changes"
            onRequestHide={this.hideUnsavedChangesDialog}
          />
        </Modal>
        <Modal isOpen={backfillDialogIsOpen} data-test="backfill-modal">
          <ConfirmDialog
            title={`Apply changes to ${this.props.activeGroupCount} classes?`}
            subtext={
              <Text>
                All class owners will receive an email explaining that their classes settings have
                changed.{' '}
                <Link href={LinkConstants.COMMS_CONTROL_BACKFILL} target="_blank">
                  Learn more
                </Link>
              </Text>
            }
            preConfirmPromise={this.handleSubmit}
            onConfirm={this.resetBackfillCheckbox}
            onCancel={this.hideBackfillDialog}
            actionButtonText="Apply changes"
            cancelButtonText="Cancel"
            onRequestHide={this.hideBackfillDialog}
            data-test="backfill-confirm-dialog"
          />
        </Modal>
        <Container size="medium" align="left" flush>
          {orgIsDemoOnly ? (
            <Text element="p">
              <Notice color="white">
                Your account is currently in demo mode. To enable this feature, sign up for a Remind
                plan.
              </Notice>
            </Text>
          ) : null}
          <TitleHeader
            title="Default class settings"
            subtext={DefaultClassSettingsConstants.SUBTEXT_FOR_ORG_TYPE[this.props.orgType]}
            textAlign="left"
          />
          {this.renderClassSearchabilityField()}
          {this.renderRequestToJoinEnabledField()}
          {this.renderParticipantMessagingStateField()}
          {this.renderBackfillCheckbox()}
          <Button
            data-test="submit-button"
            onClick={backfillExistingClasses ? this.showBackfillDialog : this.handleSubmit}
            disabled={!this.userHasChangedSettings() || orgIsDemoOnly}>
            Save
          </Button>
        </Container>
      </React.Fragment>
    );
  }
}
